import { useCallback, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'

import {
  SkipTaskCompletionDocument,
  SkipTaskCompletionMutation,
  GetStartedTaskCompletionDocument,
} from 'generated/resources/graphql'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { ChevronDown, LockLocked, Check } from '@factorialco/f0-react/icons/app'
import i18n from 'modules/core/lib/i18n'
import { useMutation } from 'modules/core/hooks/useMutation'
import type { Category } from 'modules/get_started/lib/taskCatalog'

import TaskItem from 'modules/get_started/components/TaskItem'

type Props = {
  category: Category
  completedTaskKeys: Set<string>
  locked?: boolean
  defaultExpanded?: boolean
  expandedTaskKey: string | null
  onTaskToggle: (taskKey: string) => void
  aiToursEnabled: boolean
}

const CategoryAccordion = ({
  category,
  completedTaskKeys,
  locked = false,
  defaultExpanded = false,
  expandedTaskKey,
  onTaskToggle,
  aiToursEnabled,
}: Props) => {
  const hasExpandedTask = category.tasks.some((t) => t.taskKey === expandedTaskKey)
  const [isExpanded, setIsExpanded] = useState(!locked && (defaultExpanded || hasExpandedTask))
  const [skipTask] = useMutation(SkipTaskCompletionDocument, {
    extractorFn: (data: SkipTaskCompletionMutation) =>
      data.getStartedMutations.skipTaskGetstartedTaskCompletion,
    refetchQueries: [GetStartedTaskCompletionDocument],
  })

  const handleSkipTask = useCallback(
    (taskKey: string) => {
      skipTask({ variables: { taskKey, category: category.id } })
    },
    [skipTask, category.id]
  )

  const completedCount = category.tasks.filter((t) => completedTaskKeys.has(t.taskKey)).length
  const isCategoryCompleted = category.tasks.every((t) => completedTaskKeys.has(t.taskKey))

  return (
    <Box
      padding="s12"
      gap="s12"
      border={{ all: { width: 's1', style: 'solid', color: 'grey300' } }}
      borderRadius={{ all: 'abs016' }}
      background={locked ? 'grey200' : undefined}
      style={
        locked
          ? undefined
          : {
              boxShadow:
                '0px 4px 12px -8px rgba(30, 30, 49, 0.03), 0px 1px 3px rgba(30, 30, 49, 0.03)',
            }
      }
    >
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        onClick={locked ? undefined : () => setIsExpanded(!isExpanded)}
        cursor={locked ? undefined : 'pointer'}
        borderRadius={{ all: 'abs016' }}
        paddingTop="s8"
        paddingBottom="s8"
      >
        <Box flexDirection="row" alignItems="baseline" gap="s8">
          {isCategoryCompleted && (
            <Box
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#06838B',
                flexShrink: '0',
              }}
              alignItems="center"
              justifyContent="center"
            >
              <F0Icon icon={Check} size="sm" color="#FFFFFF" />
            </Box>
          )}
          <Box gap="s2" paddingLeft="s4" paddingRight="s4">
            <Text.Normal size="300" weight="semibold" color="grey1000">
              {category.title}
            </Text.Normal>
            <Text.Normal size="200" color="grey700">
              {i18n.t('growth.get_started.completed_steps', {
                completedCount,
                totalCount: category.tasks.length,
              })}
            </Text.Normal>
          </Box>
        </Box>
        {locked ? (
          <F0Icon icon={LockLocked} size="lg" color="#0D1625" />
        ) : (
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{ display: 'flex' }}
          >
            <F0Icon icon={ChevronDown} size="lg" color="#0D1625" />
          </motion.div>
        )}
      </Box>
      <AnimatePresence initial={false}>
        {!locked && isExpanded && (
          <motion.div
            key="tasks"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.04, 0.62, 0.23, 0.98] }}
            style={{ overflow: 'hidden' }}
          >
            <Box gap="s4">
              {category.tasks.map((task) => (
                <TaskItem
                  key={task.taskKey}
                  task={task}
                  isCompleted={completedTaskKeys.has(task.taskKey)}
                  onSkip={handleSkipTask}
                  isExpanded={expandedTaskKey === task.taskKey}
                  onToggle={() => onTaskToggle(task.taskKey)}
                  aiToursEnabled={aiToursEnabled}
                />
              ))}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

export default CategoryAccordion
